import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useConsent } from "@/contexts/ConsentContext";
import { siteConfig } from "@/site.config";

export function CookieBanner() {
  const { state, acceptAll, rejectAll, customize } = useConsent();
  const [openCustomize, setOpenCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(state.analytics);
  const [marketing, setMarketing] = useState(state.marketing);

  // Banner não aparece se usuário já decidiu
  if (state.decided) return null;

  const handleSave = () => {
    customize({ analytics, marketing });
    setOpenCustomize(false);
  };

  return (
    <>
      <div
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-banner-title"
        className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-background/95 backdrop-blur-sm shadow-2xl"
      >
        <div className="mx-auto max-w-7xl px-4 py-4 sm:py-5 sm:px-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <h2
              id="cookie-banner-title"
              className="text-sm font-semibold text-foreground"
            >
              Sua privacidade
            </h2>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Usamos cookies para melhorar sua experiência e medir o desempenho
              do site. Ao clicar em "Aceitar todos", você concorda com o uso de
              cookies de análise e marketing.{" "}
              <Link
                href={siteConfig.legal.privacyPolicyPath}
                className="underline underline-offset-2 hover:text-foreground"
              >
                Saiba mais
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-nowrap shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenCustomize(true)}
              className="flex-1 sm:flex-none"
            >
              Personalizar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={rejectAll}
              className="flex-1 sm:flex-none"
            >
              Recusar
            </Button>
            <Button
              size="sm"
              onClick={acceptAll}
              className="flex-1 sm:flex-none bg-[#1a2744] hover:bg-[#0d1b2e] text-white"
            >
              Aceitar todos
            </Button>
          </div>
        </div>
      </div>

      <Sheet open={openCustomize} onOpenChange={setOpenCustomize}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Preferências de cookies</SheetTitle>
            <SheetDescription>
              Você decide quais cookies aceitar. Cookies essenciais são sempre
              ativos, eles são necessários para o site funcionar.
            </SheetDescription>
          </SheetHeader>

          <div className="px-4 py-6 space-y-6">
            <div className="flex items-start justify-between gap-4 rounded-md border p-4 opacity-70">
              <div className="flex-1">
                <Label className="text-sm font-medium">Essenciais</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Necessários para o site funcionar (segurança, preferência de
                  consent). Sempre ativos.
                </p>
              </div>
              <Switch
                checked
                disabled
                aria-label="Cookies essenciais (sempre ativos)"
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border p-4">
              <div className="flex-1">
                <Label htmlFor="cb-analytics" className="text-sm font-medium">
                  Análise
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Permite entender como você usa o site (Google Analytics). Não
                  identifica você.
                </p>
              </div>
              <Switch
                id="cb-analytics"
                checked={analytics}
                onCheckedChange={setAnalytics}
                aria-label="Cookies de análise"
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border p-4">
              <div className="flex-1">
                <Label htmlFor="cb-marketing" className="text-sm font-medium">
                  Marketing
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Usado para mostrar anúncios mais relevantes e medir resultados
                  de campanhas (Google Ads, Meta).
                </p>
              </div>
              <Switch
                id="cb-marketing"
                checked={marketing}
                onCheckedChange={setMarketing}
                aria-label="Cookies de marketing"
              />
            </div>
          </div>

          <div className="px-4 pb-6 flex flex-col gap-2">
            <Button
              onClick={handleSave}
              className="bg-[#1a2744] hover:bg-[#0d1b2e] text-white"
            >
              Salvar preferências
            </Button>
            <Button variant="outline" onClick={rejectAll}>
              Recusar todos
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
